import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useSignup } from '../../features/auth/useSignup';
import { useInviteInfo } from '../../features/customers/api';
import { useOrgInviteInfo } from '../../features/org-members/api';
import { useCustomerSubuserInviteInfo } from '../../features/customer-subusers/api';
import loginBg from '../../assets/login-bg.jpeg';
import logoImage from '../../assets/bg_transparent.png';
import './SignupPage.scss';

function extractInviteTokenFromRedirect(
  search: string
): { kind: 'customer' | 'org' | 'customer-subuser'; token: string } | null {
  const sp = new URLSearchParams(search);
  const redirect = sp.get('redirect');
  if (!redirect) return null;

  try {
    const url = new URL(redirect, window.location.origin);
    const parts = url.pathname.split('/').filter(Boolean);

    // /invite/:token
    if (parts[0] === 'invite' && parts[1]) {
      return { kind: 'customer', token: parts[1] };
    }

    // /org-invite/:token
    if (parts[0] === 'org-invite' && parts[1]) {
      return { kind: 'org', token: parts[1] };
    }

    // /customer-subuser-invite/:token
    if (parts[0] === 'customer-subuser-invite' && parts[1]) {
      return { kind: 'customer-subuser', token: parts[1] };
    }
  } catch {}

  return null;
}

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = new URLSearchParams(location.search).get('redirect') || '/settings';
  const inviteCtx = extractInviteTokenFromRedirect(location.search);

  const inviteToken = inviteCtx?.token || null;
  const inviteKind = inviteCtx?.kind || null;

  const customerInvite = useInviteInfo(
    inviteToken || '',
    inviteKind === 'customer' && !!inviteToken
  );

  const orgInvite = useOrgInviteInfo(inviteToken || '', inviteKind === 'org' && !!inviteToken);

  const customerSubuserInvite = useCustomerSubuserInviteInfo(
    inviteToken || '',
    inviteKind === 'customer-subuser' && !!inviteToken
  );

  const isLoading =
    !!customerInvite?.isLoading || !!orgInvite?.isLoading || !!customerSubuserInvite?.isLoading;

  const isError =
    !!customerInvite?.isError || !!orgInvite?.isError || !!customerSubuserInvite?.isError;

  const invite =
    inviteKind === 'customer'
      ? customerInvite?.data
      : inviteKind === 'org'
        ? orgInvite?.data
        : inviteKind === 'customer-subuser'
          ? customerSubuserInvite?.data
          : null;

  const signupMutation = useSignup();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  const inviteValid = !!inviteToken && !!invite && !isError;

  useEffect(() => {
    if (invite?.email) setEmail(invite.email);
  }, [invite]);

  useEffect(() => {
    if (invite?.name) setName(invite.name);
  }, [invite]);

  const disabled = !inviteValid || !email || !password || !name || signupMutation.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteValid) return;

    signupMutation.mutate(
      { email, password, name },
      {
        onSuccess: () => {
          navigate(redirect, { replace: true });
        },
      }
    );
  };

  if (!inviteToken) {
    return (
      <div className="signup-page" style={{ backgroundImage: `url(${loginBg})` }}>
        <div className="signup-card">
          <div className="logo">
            <img src={logoImage} alt="App Logo" className="logo-image" />
          </div>
          <div className="card" style={{ background: '#fff7ed', border: '1px solid #fed7aa' }}>
            <p style={{ margin: 0 }}>
              Sign up is by invite only. Please use the link you received in your email.
            </p>
          </div>
          <div className="actions" style={{ marginTop: 12 }}>
            <Link className="btn btn--ghost" to="/login">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="signup-page" style={{ backgroundImage: `url(${loginBg})` }}>
        <div className="signup-card">
          <div className="logo">
            <img src={logoImage} alt="App Logo" className="logo-image" />
          </div>
          <div className="skeleton" style={{ height: 160 }} />
        </div>
      </div>
    );
  }

  if (isError || !invite) {
    return (
      <div className="signup-page" style={{ backgroundImage: `url(${loginBg})` }}>
        <div className="signup-card">
          <div className="logo">
            <img src={logoImage} alt="App Logo" className="logo-image" />
          </div>
          <div className="card" style={{ background: '#fee2e2', border: '1px solid #fecaca' }}>
            <p style={{ margin: 0 }}>This invite link is invalid or expired.</p>
          </div>
          <div className="actions" style={{ marginTop: 12 }}>
            <Link className="btn btn--ghost" to="/login">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const inviteSourceLabel =
    inviteKind === 'org'
      ? invite.orgName
      : inviteKind === 'customer-subuser'
        ? invite.customerName || invite.orgName
        : invite.orgName;

  const inviteSubtitle =
    inviteKind === 'customer-subuser'
      ? 'You’re creating an account to join a customer account as a subuser.'
      : 'You’re creating an account to accept your invite.';

  return (
    <div className="signup-page" style={{ backgroundImage: `url(${loginBg})` }}>
      <div className="signup-card">
        <div className="logo">
          <img src={logoImage} alt="App Logo" className="logo-image" />
        </div>

        <div
          className="card"
          style={{ marginBottom: 12, background: '#eff6ff', border: '1px solid #bfdbfe' }}
        >
          <div style={{ fontSize: 14 }}>
            {inviteKind === 'customer-subuser' ? (
              <>
                {inviteSubtitle}{' '}
                <strong>{invite.customerName || invite.orgName || 'Customer account'}</strong>
                {invite.orgName ? (
                  <>
                    {' '}
                    under <strong>{invite.orgName}</strong>
                  </>
                ) : null}
                .
              </>
            ) : (
              <>
                You’re creating an account to accept your invite from{' '}
                <strong>{inviteSourceLabel}</strong>.
              </>
            )}
          </div>

          <div className="muted" style={{ fontSize: 12 }}>
            You must sign up with the invited email address.
          </div>
        </div>

        <form onSubmit={handleSubmit} className="signup-form">
          <div className="form-group">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full Name"
              required
            />
          </div>

          <div className="form-group">
            <input type="email" value={email} disabled placeholder="Invited Email" required />
          </div>

          <div className="form-group">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="signup-button" disabled={disabled}>
              {signupMutation.isPending ? 'Creating…' : 'Create Account'}
            </button>
          </div>

          {signupMutation.isError && (
            <p className="error-message">
              {(signupMutation.error as any)?.response?.data?.message ||
                'Something went wrong. Please try again.'}
            </p>
          )}
        </form>

        <div className="switch-auth">
          <p>Already have an account?</p>
          <Link to={`/login?redirect=${encodeURIComponent(redirect)}`} className="login-link">
            Log In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
